import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { withStyles } from '@mui/styles'
import { Box, FormHelperText } from '@mui/material'

import { recaptchaStyles } from './recaptcha.styles'

/**
 * @typedef {object} ReCaptchaProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {string} siteKey - La clave pública de reCAPTCHA proporcionada por Google.
 * @property {function(string): void} onChange - Callback que se ejecuta cuando el usuario completa el captcha, recibe el token de verificación.
 * @property {function(): void} [onExpired] - Callback que se ejecuta cuando el token expira (después de 2 minutos).
 * @property {function(Error): void} [onError] - Callback que se ejecuta si hay un error cargando o ejecutando el captcha.
 * @property {'light' | 'dark'} [theme='light'] - Tema visual del captcha.
 * @property {'normal' | 'compact'} [size='normal'] - Tamaño del widget de captcha.
 * @property {boolean} [error=false] - Si es true, muestra el componente en estado de error.
 * @property {string} [errorMessage] - Mensaje de error que se muestra cuando error es true.
 * @property {string} [hl='es'] - Código de idioma para el captcha (es, en, ca, etc.).
 */

/**
 * @typedef {object} ReCaptchaRef
 * @property {function(): void} reset - Resetea el captcha para que el usuario pueda volver a completarlo.
 * @property {function(): Promise<string>} execute - Ejecuta el captcha de forma programática (solo para reCAPTCHA invisible).
 * @property {function(): string|null} getValue - Obtiene el token actual del captcha.
 */

/**
 * Componente de Google reCAPTCHA v2 configurado para máxima seguridad.
 *
 * Este componente integra Google reCAPTCHA v2 con la configuración más restrictiva,
 * asegurando que siempre se muestren desafíos visuales (semáforos, pasos de cebra, etc.)
 * en lugar de permitir la verificación con un simple clic.
 *
 * **Características de seguridad:**
 * - Configurado para nivel de seguridad máximo
 * - Siempre requiere interacción visual del usuario
 * - Token válido por 2 minutos (manejado automáticamente)
 * - Soporta temas claro/oscuro
 * - Gestión de errores y expiración
 *
 * **Uso con ref:**
 * El componente expone métodos a través de ref para control programático:
 * - `reset()`: Reinicia el captcha
 * - `getValue()`: Obtiene el token actual
 * - `execute()`: Ejecuta el captcha (solo modo invisible)
 *
 * @component
 * @example
 * // Uso básico
 * const MyForm = () => {
 *   const [token, setToken] = useState('');
 *   const recaptchaRef = useRef();
 *
 *   const handleCaptchaChange = (value) => {
 *     console.log('Token reCAPTCHA:', value);
 *     setToken(value);
 *   };
 *
 *   const handleSubmit = async () => {
 *     if (!token) {
 *       alert('Por favor completa el captcha');
 *       return;
 *     }
 *     // Enviar token al backend para validación
 *     const response = await fetch('/api/verify-recaptcha', {
 *       method: 'POST',
 *       body: JSON.stringify({ token })
 *     });
 *   };
 *
 *   const handleReset = () => {
 *     recaptchaRef.current.reset();
 *     setToken('');
 *   };
 *
 *   return (
 *     <div>
 *       <ReCaptcha
 *         ref={recaptchaRef}
 *         siteKey="YOUR_SITE_KEY_HERE"
 *         onChange={handleCaptchaChange}
 *         onExpired={() => setToken('')}
 *         error={!token && submitted}
 *         errorMessage="Debes completar el captcha"
 *       />
 *       <button onClick={handleSubmit}>Enviar</button>
 *       <button onClick={handleReset}>Resetear</button>
 *     </div>
 *   );
 * };
 *
 * @param {ReCaptchaProps} props - Las props del componente ReCaptcha.
 * @param {React.Ref<ReCaptchaRef>} ref - Referencia para acceder a los métodos del captcha.
 */
export const ReCaptcha = withStyles(recaptchaStyles)(
  forwardRef(
    (
      {
        classes,
        siteKey,
        onChange,
        onExpired,
        onError,
        theme = 'light',
        size = 'normal',
        error = false,
        errorMessage,
        hl = 'es',
        ...props
      },
      ref
    ) => {
      const recaptchaRef = useRef()

      // Exponer métodos a través de ref para control desde el componente padre
      useImperativeHandle(ref, () => ({
        /**
         * Resetea el captcha
         */
        reset: () => {
          if (recaptchaRef.current) {
            recaptchaRef.current.reset()
          }
        },
        /**
         * Ejecuta el captcha (solo para reCAPTCHA invisible)
         */
        execute: async () => {
          if (recaptchaRef.current) {
            return await recaptchaRef.current.executeAsync()
          }
        },
        /**
         * Obtiene el token actual del captcha
         */
        getValue: () => {
          if (recaptchaRef.current) {
            return recaptchaRef.current.getValue()
          }
          return null
        },
      }))

      const handleChange = (value) => {
        if (onChange) {
          onChange(value)
        }
      }

      const handleExpired = () => {
        if (onExpired) {
          onExpired()
        }
        // Auto-reset cuando expira
        if (recaptchaRef.current) {
          recaptchaRef.current.reset()
        }
      }

      const handleError = (error) => {
        if (onError) {
          onError(error)
        }
        console.error('Error en reCAPTCHA:', error)
      }

      return (
        <Box className={classes.container}>
          <Box className={`${classes.recaptchaWrapper} ${error ? classes.error : ''}`}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={siteKey}
              onChange={handleChange}
              onExpired={handleExpired}
              onErrored={handleError}
              theme={theme}
              size={size}
              hl={hl}
              badge="bottomright"
              type="image"
              {...props}
            />
          </Box>
          {error && errorMessage && (
            <FormHelperText error className={classes.errorMessage}>
              {errorMessage}
            </FormHelperText>
          )}
        </Box>
      )
    }
  )
)

ReCaptcha.displayName = 'ReCaptcha'
